import { apiErrorCode, API_ERROR_CODES } from '#shared/apiErrorCodes'
import type { SessionCookieResponse } from '#shared/auth/sessionCookie'
import type { Api } from '~/plugins/api'

/**
 * Bare `$fetch` with `$api`'s flat signature (MAR-1131): a `string` path and an explicit `<T>`, so
 * neither call below looks its path up in Nuxt's route map. That lookup alone used 99% of
 * TypeScript's per-expression instantiation budget and tipped into `TS2589` as routes were added.
 * The cast goes through `unknown` because comparing `$fetch`'s route-map generic to `Api` exceeds
 * the stack depth too (`TS2321`). Bare `$fetch` rather than `$api` is deliberate: see
 * `app/composables/useApi.ts`. It reads the global at call time, not at module load, because Nuxt
 * installs `$fetch` after this module is evaluated.
 */
function sessionFetch(): Api {
  return $fetch as unknown as Api
}

const COOKIE_NAME = '__session'

// max-age slightly exceeds the Firebase token lifetime (3600s) to ensure
// the onIdTokenChanged refresh can replace it before it expires.
const COOKIE_MAX_AGE = 3900

/**
 * Set session cookie via server endpoint (HttpOnly).
 * Falls back to client-side cookie if the server call fails,
 * so auth still works during transient network issues.
 */
/**
 * @returns whether the server minted new identity claims for this user while handling the call. The
 * caller must then force an ID-token refresh — the token just posted does not carry them, and
 * without the refresh they arrive whenever Firebase next refreshes on its own, up to an hour later
 * (MAR-427). On any failure this reports `false`: the server's database fallback keeps the session
 * working, so a missed refresh is slow, never broken.
 */
let lastToken: string | null = null
let lastPromise: Promise<boolean> | null = null

async function postSessionCookie(token: string): Promise<boolean> {
  try {
    const result = await sessionFetch()<SessionCookieResponse>('/api/auth/session', {
      method: 'POST',
      body: { token },
    })
    const claimsRefreshed = !!result?.claims_refreshed
    if (claimsRefreshed) {
      lastToken = null
      lastPromise = null
    }
    return claimsRefreshed
  } catch (err) {
    lastToken = null
    lastPromise = null
    /**
     * `MFA_REQUIRED` (AlexAmin/marcus#899) is NOT a transient failure the fallback below exists for
     * — it is the server DEFINITIVELY refusing this exact token because the account has activated
     * two-factor auth and this token never completed it. Falling back to a client-side cookie here,
     * as this used to do unconditionally, would silently serve the caller the very stale,
     * second-factor-less session the server just rejected — completely defeating the server-side
     * enforcement in `server/utils/firebase-admin.ts`. Re-throw instead, so the caller (every caller
     * either signs the user all the way back out — `app/plugins/firebase.client.ts`'s
     * `onIdTokenChanged`, `app/plugins/api.ts`'s `onResponseError` — or is itself the code that just
     * completed a real second factor and should never see this in the first place) can react instead
     * of the app quietly limping along on a session the server will keep refusing on every request.
     */
    if (apiErrorCode(err) === API_ERROR_CODES.mfaRequired) {
      throw err
    }
    // Fallback: set client-side cookie so the app isn't broken
    document.cookie = `${COOKIE_NAME}=${token}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=None; Secure`
    console.warn('[auth] Failed to set HttpOnly session cookie, fell back to client-side cookie')
    return false
  }
}

export async function setSessionCookie(token: string): Promise<boolean> {
  if (lastToken === token && lastPromise) {
    return lastPromise
  }

  lastToken = token
  lastPromise = postSessionCookie(token)
  return lastPromise
}

/**
 * Clear the session cookie via the server endpoint — the real `__session`
 * cookie is HttpOnly, so a client-side document.cookie write can't remove it.
 * Falls back to a client-side clear for any non-HttpOnly fallback cookie.
 */
export async function clearSessionCookie() {
  lastToken = null
  lastPromise = null
  try {
    await sessionFetch()('/api/auth/session', { method: 'DELETE' })
  } catch {
    document.cookie = `${COOKIE_NAME}=; path=/; max-age=0; SameSite=None; Secure`
    console.warn('[auth] Failed to clear HttpOnly session cookie via server; cleared client fallback only')
  }
}
